import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, VStack, Text, Input, Select, Box, Button } from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { useDataLinkStore, parseCsv, normalizeTableCells } from '../../core/commands/tableModel';
import { workbookMeta, workbookRows } from '../../utils/xlsxReader';
import { runInteraction, PT } from '../../core/commands/runners/lib';
import { undoableAdd, makeEntity } from '../../core/commands/entityOps';

/**
 * DATALINK dialog (Faz 8.7, OCS data_link parity): pick a local .xlsx/.csv
 * file, choose the sheet + range (A1:D20 or a named range), preview the
 * first rows, then bind — the link registers in useDataLinkStore and a
 * table places at a picked point. CONTENT_LOCKED semantics: cells sourced
 * from the link stay read-only (they refresh through Refresh/TABLEUPDATE).
 */
export function DataLinkDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'dataLink');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const fileInput = useRef<HTMLInputElement>(null);
    const [picked, setPicked] = useState<File | null>(null);
    const [sheets, setSheets] = useState<string[]>([]);
    const [sheet, setSheet] = useState('');
    const [range, setRange] = useState('');
    const [preview, setPreview] = useState<string[][]>([]);
    const [error, setError] = useState('');
    const linkNames = useDataLinkStore((s) => Object.keys(s.links));
    const links = useDataLinkStore((s) => s.links);

    const pick = async (file: File | null) => {
        setPicked(file);
        setSheets([]); setSheet(''); setRange(''); setPreview([]); setError('');
        if (!file) return;
        try {
            if (/\.(xlsx|xlsm|xls)$/i.test(file.name)) {
                const meta = workbookMeta(await file.arrayBuffer());
                setSheets(meta.sheets);
                setSheet(meta.sheets[0] ?? '');
                if (meta.sheets[0]) {
                    setPreview(workbookRows(await file.arrayBuffer(), meta.sheets[0], '').slice(0, 8));
                }
            } else {
                const rows = parseCsv(await file.text()).slice(0, 8);
                setPreview(rows);
            }
        } catch (e) {
            setError(String(e));
        }
    };

    const changeSheet = async (next: string) => {
        setSheet(next);
        if (picked && /\.(xlsx|xlsm|xls)$/i.test(picked.name)) {
            try { setPreview(workbookRows(await picked.arrayBuffer(), next, range).slice(0, 8)); } catch { /* preview only */ }
        }
    };

    const bind = async () => {
        if (!picked) return;
        const name = picked.name.replace(/\.[^.]+$/, '');
        const store = useDataLinkStore.getState();
        store.cacheFile(name, picked);
        let rows: string[][] = [];
        if (/\.(xlsx|xlsm|xls)$/i.test(picked.name)) {
            rows = workbookRows(await picked.arrayBuffer(), sheet, range);
            if (rows.length === 0) { setError(t('ui.cad.dataLink.noPreview')); return; }
            store.setXlsx(name, rows, { source: picked.name, sheet, range: range || undefined });
        } else {
            rows = parseCsv(await picked.text());
            if (rows.length === 0) { setError(t('ui.cad.dataLink.noPreview')); return; }
            store.set(name, await picked.text(), picked.name);
        }
        onClose();
        // Bind: the table places at a picked point (CONTENT_LOCKED cells
        // refresh through Refresh/TABLEUPDATE only).
        runInteraction({
            steps: [PT(`TABLE "${name}": insertion point`)],
            complete: (pts) => {
                const p = pts[0];
                const cells = normalizeTableCells(rows);
                undoableAdd([makeEntity('table', `Table-${Date.now()}`, {
                    cells, position: [p.x, p.y, p.z],
                    colWidth: 2, rowHeight: 0.6, hasHeader: true, linkName: name,
                }, activeLayerId)]);
                return `DATALINK "${name}": table bound (${cells.length} row(s)) — linked cells are content-locked.`;
            },
        });
    };

    const refreshLink = async (name: string) => {
        const n = await useDataLinkStore.getState().refresh(name);
        if (n === null) setError(t('ui.cad.dataLink.noPreview'));
    };

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('ui.cad.dataLink.title')}
            size="lg"
            footer={<DialogButton variant="ghost" onClick={onClose}>{t('common.cancel')}</DialogButton>}
        >
            <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                    <Text fontSize="sm" color="var(--color-text-secondary)">{t('ui.cad.dataLink.file')}</Text>
                    <input
                        ref={fileInput}
                        type="file"
                        accept=".xlsx,.xlsm,.xls,.csv,.txt"
                        hidden
                        onChange={(e) => { void pick(e.target.files?.[0] ?? null); }}
                    />
                    <Button size="sm" variant="outline" onClick={() => fileInput.current?.click()}>
                        {picked ? picked.name : t('ui.cad.dataLink.pickFile')}
                    </Button>
                </HStack>
                {sheets.length > 0 && (
                    <HStack justify="space-between">
                        <Text fontSize="sm" color="var(--color-text-secondary)">{t('ui.cad.dataLink.sheet')}</Text>
                        <Select size="sm" w="220px" value={sheet} onChange={(e) => { void changeSheet(e.target.value); }}>
                            {sheets.map((s) => <option key={s} value={s}>{s}</option>)}
                        </Select>
                    </HStack>
                )}
                <HStack justify="space-between">
                    <Text fontSize="sm" color="var(--color-text-secondary)">{t('ui.cad.dataLink.range')}</Text>
                    <Input
                        size="sm" w="220px" value={range} placeholder="A1:D20"
                        bg="var(--color-bg-primary)" borderColor="var(--color-border)"
                        onChange={async (e) => {
                            const next = e.target.value;
                            setRange(next);
                            if (picked && /\.(xlsx|xlsm|xls)$/i.test(picked.name) && sheet) {
                                try { setPreview(workbookRows(await picked.arrayBuffer(), sheet, next).slice(0, 8)); } catch { /* preview only */ }
                            }
                        }}
                    />
                </HStack>
                <Box>
                    <Text fontSize="xs" color="var(--color-text-secondary)" mb={1}>{t('ui.cad.dataLink.preview')}</Text>
                    <Box borderWidth="1px" borderColor="var(--color-border)" borderRadius="var(--cad-radius-sm)" maxH="160px" overflow="auto" p={2}>
                        {preview.length === 0 ? (
                            <Text fontSize="xs" color="var(--color-text-secondary)">{t('ui.cad.dataLink.noPreview')}</Text>
                        ) : preview.map((row, i) => (
                            <Text key={i} fontSize="xs" fontFamily="mono" whiteSpace="nowrap">
                                {row.join(' | ')}
                            </Text>
                        ))}
                    </Box>
                </Box>
                {error && <Text fontSize="xs" color="red.400">{error}</Text>}
                <HStack justify="flex-end" spacing={2}>
                    <DialogButton variant="primary" onClick={() => { void bind(); }} isDisabled={!picked}>
                        {t('ui.cad.dataLink.bind')}
                    </DialogButton>
                    <DialogButton variant="ghost" onClick={onClose}>{t('common.cancel')}</DialogButton>
                </HStack>

                {linkNames.length > 0 && (
                    <Box pt={2} borderTop="1px solid" borderColor="var(--color-border)">
                        <Text fontSize="xs" color="var(--color-text-secondary)" mb={1}>
                            {t('ui.cad.dataLink.linked')}
                        </Text>
                        {linkNames.map((name) => (
                            <HStack key={name} justify="space-between" px={1} py={0.5}>
                                <Text fontSize="xs">
                                    {name} — {links[name]?.source ?? ''}
                                    {links[name]?.sheet ? ` / ${links[name].sheet}` : ''}
                                    {links[name]?.range ? ` / ${links[name].range}` : ''}
                                </Text>
                                <Button size="xs" variant="outline" onClick={() => { void refreshLink(name); }}>
                                    Refresh
                                </Button>
                            </HStack>
                        ))}
                    </Box>
                )}
            </VStack>
        </AppDialog>
    );
}
